'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function LeanBodyMassCalculator() {
  const t = useTranslations("tools.leanBodyMassCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [ageGroup, setAgeGroup] = useState<'young' | 'adult'>('adult');
  const [inputs, setInputs] = useState({
    feet: 5,
    inches: 10,
    cm: 178,
    pounds: 160,
    kg: 73,
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const calculateLBM = (heightCm: number, weightKg: number, gender: string, ageGroup: string) => {
    const results = [];

    if (ageGroup === 'adult') {
      if (gender === 'male') {
        const boer = 0.407 * weightKg + 0.267 * heightCm - 19.2;
        results.push({ formula: 'Boer', lbm: boer });

        const james = 1.1 * weightKg - 128 * Math.pow(weightKg / heightCm, 2);
        results.push({ formula: 'James', lbm: james });

        const hume = 0.32810 * weightKg + 0.33929 * heightCm - 29.5336;
        results.push({ formula: 'Hume', lbm: hume });
      } else {
        const boer = 0.252 * weightKg + 0.473 * heightCm - 48.3;
        results.push({ formula: 'Boer', lbm: boer });

        const james = 1.07 * weightKg - 148 * Math.pow(weightKg / heightCm, 2);
        results.push({ formula: 'James', lbm: james });

        const hume = 0.29569 * weightKg + 0.41813 * heightCm - 43.2933;
        results.push({ formula: 'Hume', lbm: hume });
      }
    } else {
      const eECV = 0.0215 * Math.pow(weightKg, 0.6469) * Math.pow(heightCm, 0.7236);
      const peters = 3.8 * eECV;
      results.push({ formula: 'Peters (Children)', lbm: peters });
    }

    return results;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    let heightCm: number;
    let weightKg: number;

    if (unitSystem === 'imperial') {
      if (inputs.feet < 0 || inputs.inches < 0 || inputs.pounds <= 0) {
        setError(t('errorInvalidMeasurements'));
        return;
      }
      heightCm = (inputs.feet * 30.48) + (inputs.inches * 2.54);
      weightKg = inputs.pounds * 0.453592;
    } else {
      if (inputs.cm <= 0 || inputs.kg <= 0) {
        setError(t('errorInvalidMeasurements'));
        return;
      }
      heightCm = inputs.cm;
      weightKg = inputs.kg;
    }

    const calculatedResults = calculateLBM(heightCm, weightKg, gender, ageGroup);
    setResults({ data: calculatedResults, totalWeight: weightKg });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const unitTabsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    background: '#f1f5f9',
    padding: '0.5rem',
    borderRadius: '0.75rem',
  };

  const unitTabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '0.75rem 1rem',
    border: 'none',
    background: isActive ? 'white' : 'transparent',
    color: isActive ? '#2563eb' : '#64748b',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: '0.5rem',
    transition: 'all 0.3s ease',
    boxShadow: isActive ? '0 2px 8px rgba(37, 99, 235, 0.2)' : 'none',
  });

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const formGroupFullWidthStyle: React.CSSProperties = {
    ...formGroupStyle,
    gridColumn: '1 / -1',
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  };

  const radioGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
  };

  const radioLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#475569',
  };

  const radioInputStyle: React.CSSProperties = {
    width: '1.2rem',
    height: '1.2rem',
    cursor: 'pointer',
    accentColor: '#2563eb',
  };

  const inputWrapperStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
    { padding: '1rem 1.5rem', fontSize: '1.125rem' }
  );

  const unitLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: '#f1f5f9',
    borderRadius: '0.5rem',
    color: '#64748b',
    fontWeight: 600,
    minWidth: '80px',
    justifyContent: 'center',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1.125rem 2.25rem', fontSize: '1.15rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.2rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: error ? 'block' : 'none',
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsTableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  };

  const tableHeaderStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
  };

  const thStyle = getResponsiveStyle(
    { textAlign: 'left' as const, fontWeight: 600 },
    { padding: '0.75rem 0.5rem', fontSize: '0.9rem' },
    { padding: '0.875rem 0.75rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #e2e8f0', color: '#334155' },
    { padding: '0.75rem 0.5rem' },
    { padding: '0.875rem 0.75rem' },
    { padding: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle: React.CSSProperties = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const referenceStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: colors.textSecondary,
    marginBottom: '0.5rem',
    lineHeight: 1.5,
  };

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={unitTabsStyle}>
              <button
                type="button"
                style={unitTabStyle(unitSystem === 'imperial')}
                onClick={() => setUnitSystem('imperial')}
              >
                {t('usUnits')}
              </button>
              <button
                type="button"
                style={unitTabStyle(unitSystem === 'metric')}
                onClick={() => setUnitSystem('metric')}
              >
                {t('metricUnits')}
              </button>
            </div>

            <div style={formGridStyle}>
              <div style={formGroupFullWidthStyle}>
                <label style={labelStyle}>{t('gender')}</label>
                <div style={radioGroupStyle}>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      style={radioInputStyle}
                      checked={gender === 'male'}
                      onChange={() => setGender('male')}
                    />
                    {t('male')}
                  </label>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      style={radioInputStyle}
                      checked={gender === 'female'}
                      onChange={() => setGender('female')}
                    />
                    {t('female')}
                  </label>
                </div>
              </div>

              <div style={formGroupFullWidthStyle}>
                <label style={labelStyle}>{t('ageQuestion')}</label>
                <div style={radioGroupStyle}>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      style={radioInputStyle}
                      checked={ageGroup === 'young'}
                      onChange={() => setAgeGroup('young')}
                    />
                    {t('yes')}
                  </label>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      style={radioInputStyle}
                      checked={ageGroup === 'adult'}
                      onChange={() => setAgeGroup('adult')}
                    />
                    {t('no')}
                  </label>
                </div>
              </div>

              {unitSystem === 'imperial' ? (
                <>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t('height')}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        style={inputStyle}
                        min="0"
                        max="8"
                        step="1"
                        value={inputs.feet}
                        onChange={(e) => setInputs({ ...inputs, feet: parseFloat(e.target.value) || 0 })}
                        required
                      />
                      <span style={unitLabelStyle}>{t('feet')}</span>
                      <input
                        type="number"
                        style={inputStyle}
                        min="0"
                        max="11"
                        step="0.1"
                        value={inputs.inches}
                        onChange={(e) => setInputs({ ...inputs, inches: parseFloat(e.target.value) || 0 })}
                        required
                      />
                      <span style={unitLabelStyle}>{t('inches')}</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t('weight')}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        style={inputStyle}
                        min="0"
                        max="1000"
                        step="0.1"
                        value={inputs.pounds}
                        onChange={(e) => setInputs({ ...inputs, pounds: parseFloat(e.target.value) || 0 })}
                        required
                      />
                      <span style={unitLabelStyle}>{t('pounds')}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t('height')}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        style={inputStyle}
                        min="0"
                        max="300"
                        step="0.1"
                        value={inputs.cm}
                        onChange={(e) => setInputs({ ...inputs, cm: parseFloat(e.target.value) || 0 })}
                        required
                      />
                      <span style={unitLabelStyle}>{t('cm')}</span>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t('weight')}</label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        style={inputStyle}
                        min="0"
                        max="500"
                        step="0.1"
                        value={inputs.kg}
                        onChange={(e) => setInputs({ ...inputs, kg: parseFloat(e.target.value) || 0 })}
                        required
                      />
                      <span style={unitLabelStyle}>{t('kg')}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button type="submit" style={calculateButtonStyle}>
              {t('calculateButton')}
            </button>
            <div style={errorMessageStyle}>{error}</div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ ...h2Style, textAlign: 'center' as const, marginBottom: '1.5rem' }}>
                {t('resultsTitle')}
              </h3>
              <table style={resultsTableStyle}>
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th style={thStyle}>{t('formula')}</th>
                    <th style={thStyle}>{t('leanBodyMass')}</th>
                    <th style={thStyle}>{t('bodyFat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.data.map((result: any, index: number) => {
                    const lbmKg = result.lbm;
                    const lbmLbs = lbmKg * 2.20462;
                    const lbmPercent = (lbmKg / results.totalWeight) * 100;
                    const fatPercent = 100 - lbmPercent;

                    return (
                      <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: '#1e293b' }}>
                          {result.formula}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 700, color: '#2563eb', fontSize: '1.1rem' }}>
                            {lbmLbs.toFixed(1)} lbs
                          </span>
                          <span style={{ color: '#64748b', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                            ({lbmPercent.toFixed(0)}%)
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                            {fatPercent.toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.3rem', marginTop: '1.5rem' }}>{t('formulasTitle')}</h3>
        <p style={paragraphStyle}>{t('formulasParagraph')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('boerFormula')}</strong> {t('boerDescription')}</li>
          <li style={listItemStyle}><strong>{t('jamesFormula')}</strong> {t('jamesDescription')}</li>
          <li style={listItemStyle}><strong>{t('humeFormula')}</strong> {t('humeDescription')}</li>
          <li style={listItemStyle}><strong>{t('petersFormula')}</strong> {t('petersDescription')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: '1.3rem', marginTop: '1.5rem' }}>{t('importanceTitle')}</h3>
        <p style={paragraphStyle}>{t('importanceParagraph')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('importance1')}</li>
          <li style={listItemStyle}>{t('importance2')}</li>
          <li style={listItemStyle}>{t('importance3')}</li>
          <li style={listItemStyle}>{t('importance4')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: '1.3rem', marginTop: '1.5rem' }}>{t('limitationsTitle')}</h3>
        <p style={paragraphStyle}>{t('limitationsParagraph')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('limitation1')}</li>
          <li style={listItemStyle}>{t('limitation2')}</li>
          <li style={listItemStyle}>{t('limitation3')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          1. <a href="https://www.healthkart.com/connect/lean-body-mass-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            3 Formulas to Use on Lean Body Mass Calculator - HealthKart
          </a>
        </div>
        <div style={referenceStyle}>
          2. <a href="https://tryfittrack.com/blogs/health-fitness/lean-body-mass" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Lean Body Mass, Explained - FitTrack
          </a>
        </div>
        <div style={referenceStyle}>
          3. <a href="https://www.calculator.net/lean-body-mass-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Lean Body Mass Calculator - Calculator.net
          </a>
        </div>
        <div style={referenceStyle}>
          4. <a href="https://toneopfit.com/blogs/calculate-lean-body-mass" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            How To Calculate Lean Body Mass? Know It Here! - ToneOpFit
          </a>
        </div>
        <div style={referenceStyle}>
          5. <a href="https://www.medicalnewstoday.com/articles/muscle-mass-percentage" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Muscle mass percentage: Benefits and how to calculate it - Medical News Today
          </a>
        </div>
        <div style={referenceStyle}>
          6. <a href="https://www.healthline.com/health/muscle-mass-percentage" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Muscle Mass Percentage Averages and How to Calculate It - Healthline
          </a>
        </div>
        <div style={referenceStyle}>
          7. <a href="https://en.wikipedia.org/wiki/Lean_body_mass" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Lean body mass - Wikipedia
          </a>
        </div>
        <div style={referenceStyle}>
          8. <a href="https://inbodycanada.ca/body-composition/lean-body-mass-and-muscle-mass-whats-the-difference/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {"Lean Body Mass and Muscle Mass – What's the Difference? - InBody Canada"}
          </a>
        </div>
      </section>
    </div>
  );
}